import { Building, CheckCircle, Mail, Settings } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { gmailUpdateOrgSignature } from 'sprites-types/workflow/tools/gmailUpdateOrgSignature'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GmailUpdateOrgSignatureDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(gmailUpdateOrgSignature, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const signatureHtml = inputs.signatureHtml || inputs.signature || inputs.html || ''
  const domain = inputs.domain || inputs.organization || inputs.org || ''
  const success = (result as any)?.success || (result as any)?.updated || false
  const message =
    (result as any)?.message ||
    (result as any)?.status ||
    'Organization signature updated successfully'
  const usersAffected = (result as any)?.usersAffected || (result as any)?.userCount || 0

  const isPlainText = !signatureHtml.includes('<')
  const previewText = isPlainText ? signatureHtml : signatureHtml.replace(/<[^>]*>/g, '').trim()

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="px-4 py-3">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-orange-100 p-2">
              {success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <Building className="h-5 w-5 text-orange-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <Mail className="h-4 w-4 text-orange-600" />
              <div className="text-sm font-semibold text-gray-900">
                Organization Signature Updated
              </div>
              {success && <CheckCircle className="h-4 w-4 text-green-600" />}
            </div>

            {domain && (
              <div className="mb-3 text-xs text-gray-600">
                Domain: <span className="font-medium">{domain}</span>
              </div>
            )}

            <div className="mb-3 text-xs text-gray-700">{message}</div>

            {usersAffected > 0 && (
              <div className="mb-3 flex items-center gap-1 text-xs text-gray-600">
                <Settings className="h-3 w-3" />
                <span>{usersAffected} users affected</span>
              </div>
            )}

            {previewText && previewText.length > 0 && (
              <div className="mb-3">
                <div className="mb-2 text-xs font-medium text-gray-700">Signature Preview:</div>
                <div className="max-h-32 overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-3">
                  {isPlainText ? (
                    <div className="whitespace-pre-wrap text-xs text-gray-800">
                      {previewText.length > 200 ? `${previewText.slice(0, 200)}...` : previewText}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-800">
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            signatureHtml.length > 300
                              ? `${signatureHtml.slice(0, 300)}...`
                              : signatureHtml,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadGmailUpdateOrgSignature(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function gmailUpdateOrgSignatureToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(gmailUpdateOrgSignature, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const signatureHtml = inputs.signatureHtml || inputs.signature || inputs.html || ''
  const domain = inputs.domain || inputs.organization || inputs.org || ''
  const success = (result as any)?.success || (result as any)?.updated || false
  const message =
    (result as any)?.message || (result as any)?.status || 'Organization signature updated'
  const usersAffected = (result as any)?.usersAffected || (result as any)?.userCount || 0

  return `## Gmail: Update Organization Signature

**Status:** ${success ? '✅ Success' : '❌ Failed'}
${domain ? `**Domain:** ${domain}` : ''}
**Message:** ${message}
${usersAffected > 0 ? `**Users Affected:** ${usersAffected}` : ''}

${
  signatureHtml
    ? `**Signature Content:**
\`\`\`html
${signatureHtml}
\`\`\``
    : ''
}

---
`
}

export const GmailUpdateOrgSignatureDisplay = createWorkflowToolComponent(
  GmailUpdateOrgSignatureDisplayComponent,
  downloadGmailUpdateOrgSignature,
  gmailUpdateOrgSignatureToMarkdown
)
